import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Sparkles, CheckCircle2, MessageCircle, ChevronDown, ChevronUp, HeartHandshake } from 'lucide-react';

const CORPORATE_MESSAGE = `En Natur Autoescuela no admitimos alumnos de forma ilimitada.

Creemos que una formación de calidad requiere tiempo, planificación y capacidad real de atención.

Antes de aceptar nuevas incorporaciones revisamos nuestra disponibilidad, profesorado, vehículos, horarios y capacidad organizativa.

Nuestro objetivo no es matricular al mayor número posible de personas, sino asegurarnos de que cada alumno pueda recibir la atención, seguimiento y planificación que necesita.

Preferimos revisar cada solicitud antes de admitir nuevos alumnos para evitar situaciones en las que una persona contrate un servicio que posteriormente no podamos prestar con los estándares de calidad que consideramos adecuados.`;

const STATE_NOTICES = {
  espera: 'Actualmente gestionamos las nuevas incorporaciones mediante lista de espera. Puedes dejarnos tu solicitud y te avisaremos en cuanto podamos valorarla.',
  pausadas: 'Actualmente estamos revisando nuestra capacidad y disponibilidad para mantener la calidad del servicio.\nSi lo deseas, puedes dejarnos tu solicitud y te avisaremos cuando podamos valorar nuevas incorporaciones.',
};
import { api, CONTACT } from '../lib/api';
import { toast } from 'sonner';

const START_OPTIONS = ['Lo antes posible', 'En las próximas semanas', 'En 1-3 meses', 'Solo me estoy informando'];

function YesNo({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-xs font-bold text-stone-500 mb-1.5">{label}</label>
      <div className="flex gap-2">
        {[[true, 'Sí'], [false, 'No']].map(([v, l]) => (
          <button key={l} type="button" onClick={() => onChange(v)} className={`px-5 py-2 rounded-full text-sm font-bold border transition-colors ${value === v ? 'bg-stone-800 text-white border-stone-800' : 'bg-white text-stone-600 border-stone-300 hover:border-stone-500'}`} data-testid={`${testid}-${l.toLowerCase()}`}>
            {l}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Admision() {
  const [params] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [admState, setAdmState] = useState('abiertas');
  const [whyOpen, setWhyOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', email: '', residence: '', course: '',
    theoryPassed: false, fromOtherSchool: false, transfer: false,
    startWhen: START_OPTIONS[0], notes: '',
  });

  useEffect(() => {
    document.title = 'Solicitud de admisión · Natur Autoescuela';
    api.getScmeSettingsCached().then((s) => setAdmState(s.admissionsState || 'abiertas')).catch(() => {});
    api.getProducts().then((ps) => {
      setProducts(ps);
      const pre = ps.find((p) => p.slug === params.get('curso'));
      if (pre) setForm((f) => ({ ...f, course: pre.name }));
    }).catch(() => {});
    return () => { document.title = 'Natur Autoescuela Store'; };
    // eslint-disable-next-line
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Indica tu nombre'); return; }
    if (!form.phone.trim() && !form.email.trim()) { toast.error('Indica un teléfono o email de contacto'); return; }
    if (!form.residence.trim()) { toast.error('Indica dónde resides'); return; }
    setSending(true);
    try {
      await api.createAdmission({ ...form, source: 'web' });
      setSent(true);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar la solicitud');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    const waitlist = admState !== 'abiertas';
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="admission-success">
        <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="w-8 h-8 text-teal-600" />
        </div>
        <h1 className="text-2xl md:text-3xl font-black text-stone-800 mb-3">Solicitud recibida</h1>
        <p className="text-stone-600 leading-relaxed mb-2">Tu solicitud queda en estado <strong>{waitlist ? 'Lista de espera' : 'Pendiente de revisión'}</strong>.</p>
        <p className="text-sm text-stone-500 leading-relaxed mb-8">
          {waitlist
            ? 'Te avisaremos en cuanto podamos valorar nuevas incorporaciones manteniendo la calidad de servicio que consideramos adecuada.'
            : 'Revisaremos la disponibilidad y nuestra capacidad de atención en tu zona. Te contactaremos lo antes posible con el resultado: aceptación, lista de espera o alternativas.'}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="bg-green-500 hover:bg-green-600 transition-colors text-white font-bold text-sm px-6 py-3 rounded-full flex items-center gap-2 justify-center" data-testid="admission-whatsapp">
            <MessageCircle className="w-4 h-4" /> ¿Dudas? Escríbenos por WhatsApp
          </a>
          <Link to="/" className="border border-stone-300 hover:border-stone-500 transition-colors text-stone-700 font-bold text-sm px-6 py-3 rounded-full" data-testid="admission-back-home">Volver al inicio</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="admission-page">
      {/* Introducción de ANA */}
      <div className="bg-stone-900 text-white rounded-xl p-6 mb-8 flex flex-col sm:flex-row items-center sm:items-start gap-5" data-testid="admission-intro">
        <img src="/ana-bust.png" alt="ANA - Asistente de admisión de Natur Autoescuela" className="w-28 h-28 rounded-xl object-cover object-top border-2 border-amber-400 bg-white shrink-0" data-testid="admission-ana-image" />
        <div>
          <p className="font-black mb-1 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> Hola, soy ANA.</p>
          <p className="text-sm text-stone-300 leading-relaxed">
            Gracias por tu interés en Natur Autoescuela. Nuestros servicios formativos no son de acceso libre.
            Antes de admitir nuevos alumnos revisamos la disponibilidad y nuestra capacidad de atención para
            garantizar un servicio adecuado. Para comenzar necesito conocer algunos datos.
          </p>
        </div>
      </div>

      {/* Aviso según estado global de admisiones */}
      {STATE_NOTICES[admState] && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6" data-testid="admission-state-notice">
          <p className="text-sm text-stone-700 leading-relaxed whitespace-pre-line">{STATE_NOTICES[admState]}</p>
        </div>
      )}

      {/* Por qué trabajamos por admisión (mensaje corporativo) */}
      <div className="bg-white border border-stone-200 rounded-xl mb-8 overflow-hidden" data-testid="admission-why">
        <button type="button" onClick={() => setWhyOpen(!whyOpen)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-stone-50 transition-colors" data-testid="admission-why-toggle">
          <HeartHandshake className="w-5 h-5 text-amber-500 shrink-0" />
          <span className="font-bold text-stone-800 text-sm flex-1">¿Por qué trabajamos por admisión y no por venta ilimitada?</span>
          {whyOpen ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
        </button>
        {whyOpen && (
          <p className="px-5 pb-5 text-sm text-stone-600 leading-relaxed whitespace-pre-line" data-testid="admission-why-text">{CORPORATE_MESSAGE}</p>
        )}
      </div>

      <form onSubmit={submit} className="bg-white border border-stone-200 rounded-xl p-6 md:p-8 space-y-5" data-testid="admission-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-xs font-bold text-stone-500 mb-1">Nombre completo *</label>
            <input value={form.name} onChange={set('name')} className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-name" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Teléfono *</label>
            <input value={form.phone} onChange={set('phone')} className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-phone" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">Email</label>
            <input type="email" value={form.email} onChange={set('email')} className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-email" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">¿Dónde resides actualmente? *</label>
            <input value={form.residence} onChange={set('residence')} placeholder="Ciudad / zona" className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-residence" />
          </div>
          <div>
            <label className="block text-xs font-bold text-stone-500 mb-1">¿Qué permiso o curso te interesa?</label>
            <select value={form.course} onChange={set('course')} className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-course">
              <option value="">Selecciona…</option>
              {products.map((p) => <option key={p.id} value={p.name}>{p.name}</option>)}
              <option value="Otro">Otro / no lo sé todavía</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-1">
          <YesNo label="¿Tienes el examen teórico aprobado?" value={form.theoryPassed} onChange={(v) => setForm({ ...form, theoryPassed: v })} testid="admission-theory" />
          <YesNo label="¿Vienes de otra autoescuela?" value={form.fromOtherSchool} onChange={(v) => setForm({ ...form, fromOtherSchool: v })} testid="admission-otherschool" />
          <YesNo label="¿Es un traslado de expediente?" value={form.transfer} onChange={(v) => setForm({ ...form, transfer: v })} testid="admission-transfer" />
        </div>

        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1">¿Cuándo te gustaría comenzar?</label>
          <select value={form.startWhen} onChange={set('startWhen')} className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-start">
            {START_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-stone-500 mb-1">Observaciones (opcional)</label>
          <textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="Cualquier detalle que debamos conocer" className="w-full border border-stone-300 rounded px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" data-testid="admission-notes" />
        </div>

        <button type="submit" disabled={sending} className="btn-natur w-full py-3.5 rounded font-bold text-sm disabled:opacity-50" data-testid="admission-submit">
          {sending ? 'Enviando…' : admState === 'abiertas' ? 'Enviar solicitud de admisión' : 'Unirme a la lista de espera'}
        </button>
        <p className="text-[11px] text-stone-400 text-center leading-relaxed">
          El envío de esta solicitud no garantiza plaza. Natur Autoescuela revisará la disponibilidad y capacidad
          de atención antes de confirmar la admisión.
        </p>
      </form>
    </div>
  );
}
